// src/pages/PathManagePatient/InvoiceModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import {
  invalidateOrgPathLabReportsLiteCache,
  updateResultStatusBilled,
} from "../../services/pathLabTestReportService";
import { updatePatientPathologyStatusBilled } from "../../services/PatientPathologyService";
import { getPatientById } from "../../services/patientService";
import { getLabTestById } from "../../services/labtestService";
import { createInvoice } from "../../services/invoiceService";
import { uploadAttachment } from "../../services/uploadService";
import { useAuth } from "../../context/AuthContext";
import dayjs from "dayjs";
import {
  getOrganizationId,
  getOrganizationName,
  fetchOrganizationProfile,
} from "../../utils/organizationUtils";
import { resolveInvoicePrescriptionId } from "../../utils/invoiceRelationUtils";
import {
  buildPathologyInvoiceDocumentHtml,
  printPatientSummaryDocument,
} from "../../utils/patientSummaryDocumentUtils";
import {
  DENSE_FIELD_PROPS,
  FORM_CANCEL_BUTTON_SX,
  FORM_SUBMIT_BUTTON_SX,
  STANDARD_FORM_DIALOG_ACTIONS_SX,
  STANDARD_FORM_DIALOG_PAPER_SX,
  STANDARD_FORM_DIALOG_TITLE_SX,
} from "../../utils/formModalStyles";
import {
  InvoiceAttachmentControl,
  InvoiceSummarySection,
  calcInvoiceGstBreakdown,
  formatInvoiceInr,
  resolveAttachmentUrl,
} from "../../components/InvoiceFormShared";
import { useOrganizationGstPercentage } from "../../hooks/useOrganizationGstPercentage";

const formatPatientName = (patient) => {
  if (!patient) return "";
  if (patient.name) return patient.name;
  const combined = `${patient.firstName || ""} ${patient.lastName || ""}`.trim();
  return combined;
};

const resolveInvoiceTestNames = (test, invoiceDetails) => {
  let testNames = [];

  if (invoiceDetails?.labTestNames) {
    testNames = Array.isArray(invoiceDetails.labTestNames)
      ? invoiceDetails.labTestNames
      : [invoiceDetails.labTestNames];
  } else if (test?.patientLabTests && Array.isArray(test.patientLabTests) && test.patientLabTests.length > 0) {
    testNames = test.patientLabTests.map((t) => t.testName).filter(Boolean);
  } else if (test?.testName) {
    testNames = [test.testName];
  }

  return Array.from(new Set(testNames.filter(Boolean)));
};

const buildFallbackTestLineItems = async (test, invoiceDetails) => {
  const names = resolveInvoiceTestNames(test, invoiceDetails);
  const patientLabTests = test?.patientLabTests || [];
  const normalize = (value) => (value || "").toString().trim().toLowerCase();

  return Promise.all(
    names.map(async (name) => {
      const matchingTest = patientLabTests.find((item) => normalize(item.testName) === normalize(name));
      const labTestId = matchingTest?.labTestId || matchingTest?.testId;
      let amount = 0;

      if (labTestId) {
        try {
          const labTest = await getLabTestById(labTestId);
          amount = Number(labTest?.amount) || 0;
        } catch (_) {
          amount = 0;
        }
      }

      return { testName: name, amount, labTestId: labTestId || null };
    })
  );
};

const InvoiceModal = ({ open, onClose, invoiceDetails, reportId, test, refetchPathLabTestList }) => {
  const { user } = useAuth();
  const orgGstPercentage = useOrganizationGstPercentage(user, { enabled: open });
  const [billClicked, setBillClicked] = useState(false);
  const [invoiceCreated, setInvoiceCreated] = useState(false); // Print only after Bill succeeds
  const [patientInfo, setPatientInfo] = useState(null);
  const [testLineItems, setTestLineItems] = useState([]);
  const [orgProfile, setOrgProfile] = useState({
    name: "",
    address: "",
    phone: "",
  });
  const [paymentMode, setPaymentMode] = useState({ mode: "cash" });
  const [particulars, setParticulars] = useState("");
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentName, setAttachmentName] = useState("");

  const organizationName =
    orgProfile.name ||
    invoiceDetails?.fromName ||
    getOrganizationName(user) ||
    "Hospital";
  const testNames = resolveInvoiceTestNames(test, invoiceDetails);
  const patientName =
    formatPatientName(patientInfo) || invoiceDetails?.patientName || test?.patientName || "N/A";
  const referringDoctor = test?.doctorName || "N/A";

  const subTotal = Number(invoiceDetails?.amount) || 0;
  const gstPercentage =
    invoiceDetails?.gstPercentage != null
      ? Number(invoiceDetails.gstPercentage)
      : Number(orgGstPercentage) || 0;
  const gstBreakdown = useMemo(
    () => calcInvoiceGstBreakdown(subTotal, gstPercentage),
    [subTotal, gstPercentage]
  );
  const sgst = invoiceDetails?.sgst != null ? Number(invoiceDetails.sgst) : gstBreakdown.sgst;
  const cgst = invoiceDetails?.cgst != null ? Number(invoiceDetails.cgst) : gstBreakdown.cgst;
  const totalAmount =
    invoiceDetails?.totalAmount != null
      ? Number(invoiceDetails.totalAmount)
      : gstBreakdown.totalAmount;

  const resetPaymentFields = () => {
    setPaymentMode({ mode: "cash" });
    setParticulars("");
    setAttachmentFile(null);
    setAttachmentName("");
  };

  const isBillEnabled = () => {
    if (billClicked) return false;
    if (!paymentMode?.mode) return false;
    if (paymentMode.mode === "credit" && !paymentMode.organizationId) return false;
    return true;
  };

  useEffect(() => {
    if (!open) {
      setBillClicked(false);
      setInvoiceCreated(false);
      setPatientInfo(null);
      setTestLineItems([]);
      setOrgProfile({ name: "", address: "", phone: "" });
      resetPaymentFields();
      return;
    }

    const loadInvoiceContext = async () => {
      const resolvedPatientId = test?.patientId || invoiceDetails?.patientId;
      if (resolvedPatientId) {
        try {
          const patient = await getPatientById(resolvedPatientId);
          setPatientInfo(patient || null);
        } catch (error) {
          console.warn("[InvoiceModal] Could not load patient details:", error);
          setPatientInfo(null);
        }
      }

      if (Array.isArray(invoiceDetails?.labTestLineItems) && invoiceDetails.labTestLineItems.length > 0) {
        setTestLineItems(invoiceDetails.labTestLineItems);
      } else {
        try {
          const fallbackItems = await buildFallbackTestLineItems(test, invoiceDetails);
          setTestLineItems(fallbackItems);
        } catch (error) {
          console.warn("[InvoiceModal] Could not load test rates:", error);
          const names = resolveInvoiceTestNames(test, invoiceDetails);
          setTestLineItems(names.map((name) => ({ testName: name, amount: 0 })));
        }
      }

      try {
        const orgName = getOrganizationName(user) || "Hospital";
        const organizationId = getOrganizationId(user);
        let address = "";
        let phone = "";
        if (organizationId) {
          const profile = await fetchOrganizationProfile(organizationId);
          address = profile?.address || "";
          phone = profile?.phone || "";
        }
        setOrgProfile({ name: orgName, address, phone });
      } catch (error) {
        console.warn("[InvoiceModal] Could not load organization profile:", error);
        setOrgProfile({
          name: getOrganizationName(user) || "Hospital",
          address: "",
          phone: "",
        });
      }
    };

    resetPaymentFields();
    loadInvoiceContext();
  }, [open, user, test, invoiceDetails]);

  const markTestsBilled = async () => {
    const ids =
      test?.reportIds && Array.isArray(test.reportIds)
        ? test.reportIds
        : reportId
          ? [reportId]
          : [];

    if (ids.length === 0) {
      console.warn(`[InvoiceModal] No report IDs found to update. Test:`, test);
      return;
    }

    const successfulUpdates = [];
    const failedUpdates = [];
    const labTestIds = test?.patientLabTests?.map((t) => t.labTestId).filter(Boolean) || [];

    for (const id of ids) {
      try {
        const response = await updateResultStatusBilled(id);
        successfulUpdates.push({ id, response });
      } catch (reportError) {
        console.warn(`[InvoiceModal] Failed to update report ${id} status to BILLED:`, reportError);
        failedUpdates.push({ id, type: "report", error: reportError });
      }
    }

    const testIdsToUpdate = new Set();
    successfulUpdates.forEach(({ response }) => {
      if (response?.testId) {
        testIdsToUpdate.add(response.testId);
      }
    });
    labTestIds.forEach((id) => testIdsToUpdate.add(id));

    for (const testId of testIdsToUpdate) {
      try {
        await updatePatientPathologyStatusBilled(test.patientId, test.id, testId);
      } catch (pathError) {
        console.warn(`[InvoiceModal] Failed to update patient_pathology for labTestId ${testId}:`, pathError);
        failedUpdates.push({ id: testId, type: "patient_pathology", error: pathError });
      }
    }

    if (successfulUpdates.length > 0) {
      console.log(`[InvoiceModal] Successfully updated ${successfulUpdates.length} report(s) to BILLED`);
    }
    if (failedUpdates.length > 0) {
      console.warn(`[InvoiceModal] Failed to update ${failedUpdates.length} item(s).`);
      console.warn(`[InvoiceModal] Failed items:`, failedUpdates);
    }
  };

  const handleBill = async () => {
    if (!isBillEnabled()) return;
    setBillClicked(true);

    try {
      const paymentModeValue = (paymentMode?.mode || "cash").toUpperCase();
      const creditOrganizationId =
        paymentModeValue === "CREDIT" ? paymentMode?.organizationId || null : null;

      let attachmentUrl = null;
      if (attachmentFile instanceof File) {
        const uploaded = await uploadAttachment(attachmentFile);
        attachmentUrl = resolveAttachmentUrl(uploaded);
      }

      const fromName = organizationName || "Aryan Hospital";
      const hospitalOrgId =
        invoiceDetails?.organizationId || getOrganizationId(user) || undefined;
      const { sgst: calcSgst, cgst: calcCgst, totalAmount: calcTotal } =
        calcInvoiceGstBreakdown(subTotal, gstPercentage);

      const createdInvoice = await createInvoice({
        patientId: invoiceDetails?.patientId || test?.patientId,
        doctorId:
          invoiceDetails?.doctorId ||
          invoiceDetails?.doctor_id ||
          test?.doctorId ||
          test?.doctor_id
            ? Number(
                invoiceDetails?.doctorId ||
                  invoiceDetails?.doctor_id ||
                  test?.doctorId ||
                  test?.doctor_id
              )
            : undefined,
        appointmentId:
          invoiceDetails?.appointmentId ||
          invoiceDetails?.appointment_id ||
          test?.appointmentId ||
          test?.appointment_id
            ? Number(
                invoiceDetails?.appointmentId ||
                  invoiceDetails?.appointment_id ||
                  test?.appointmentId ||
                  test?.appointment_id
              )
            : undefined,
        prescriptionId: resolveInvoicePrescriptionId(
          invoiceDetails?.prescriptionId,
          invoiceDetails?.prescription_id,
          test?.prescriptionId,
          test?.prescription_id,
          test?.id
        ),
        particulars: particulars?.trim() || invoiceDetails?.particulars || "PATHOLOGYTEST",
        invoiceType: "PATHOLOGYTEST",
        fromName,
        toName:
          invoiceDetails?.toName ||
          formatPatientName(patientInfo) ||
          test?.patientName ||
          null,
        amount: subTotal,
        sgst: calcSgst,
        cgst: calcCgst,
        totalAmount: calcTotal,
        labTestIds: invoiceDetails?.labTestIds || [],
        organizationId: hospitalOrgId,
        paymentMode: paymentModeValue,
        creditOrganizationId,
        ...(attachmentUrl ? { attachment: attachmentUrl } : {}),
      });

      console.log("[InvoiceModal] Invoice created:", createdInvoice?.id || createdInvoice);

      await markTestsBilled();

      invalidateOrgPathLabReportsLiteCache();
      setInvoiceCreated(true);
      refetchPathLabTestList();
      // Keep modal open so user can Print after billing
    } catch (error) {
      console.error("Error billing the test:", error);
      setBillClicked(false);
      alert("Error creating invoice. Please check the console for details.");
    }
  };

  const invoiceDate = invoiceDetails?.date
    ? dayjs(invoiceDetails.date).format("DD MMM YYYY")
    : dayjs().format("DD MMM YYYY");

  const printableItems =
    testLineItems.length > 0
      ? testLineItems
      : testNames.map((name) => ({ testName: name, amount: 0 }));

  const printInvoice = () => {
    const resolvedPatientId =
      patientInfo?.id ||
      invoiceDetails?.patientId ||
      test?.patientId ||
      "N/A";
    const resolvedPatientName =
      patientName ||
      formatPatientName(patientInfo) ||
      invoiceDetails?.patientName ||
      test?.patientName ||
      "Patient";

    const bodyHtml = buildPathologyInvoiceDocumentHtml({
      hospitalName: organizationName,
      hospitalAddress: orgProfile.address || "",
      hospitalPhone: orgProfile.phone || "",
      visitDate: invoiceDate,
      doctorName: referringDoctor,
      patient: {
        id: resolvedPatientId,
        name: resolvedPatientName,
        gender: patientInfo?.gender || "",
        age: patientInfo?.age || "",
        dob: patientInfo?.dob || "",
        mobile: patientInfo?.mobile || patientInfo?.phone || "",
        address: patientInfo?.address || "",
      },
      tests: printableItems.map((item) => ({
        testName: item.testName,
        amount: item.amount,
        rateLabel: formatInvoiceInr(item.amount),
      })),
      summaryLines: [
        { label: "Subtotal (₹)", value: formatInvoiceInr(subTotal) },
        {
          label: `GST @ ${gstPercentage}% (₹)`,
          value: formatInvoiceInr(sgst + cgst),
        },
        {
          label: "Total Payable Amount (₹)",
          value: formatInvoiceInr(totalAmount),
        },
      ],
      notes:
        particulars?.trim() ||
        "This is a computer-generated invoice and does not require a signature.",
      prescriptionId:
        resolveInvoicePrescriptionId(
          invoiceDetails?.prescriptionId,
          invoiceDetails?.prescription_id,
          test?.prescriptionId,
          test?.prescription_id,
          test?.id
        ) ?? "",
      appointmentId:
        invoiceDetails?.appointmentId ||
        invoiceDetails?.appointment_id ||
        test?.appointmentId ||
        test?.appointment_id ||
        "",
    });

    printPatientSummaryDocument({
      title: "Lab Test Invoice",
      bodyHtml,
    });
  };

  return (
      <Dialog
      open={open}
      onClose={billClicked && !invoiceCreated ? undefined : onClose}
      maxWidth="md"
      fullWidth
      sx={STANDARD_FORM_DIALOG_PAPER_SX}
    >
      <DialogTitle sx={STANDARD_FORM_DIALOG_TITLE_SX}>Lab Test Invoice</DialogTitle>

      <DialogContent>
        <Grid container spacing={1} sx={{ mb: 1.5 }}>
          <Grid item xs={12} md={4}>
            <Typography variant="body2">
              <strong>Patient Name:</strong> {patientName}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2">
              <strong>Doctor:</strong> {referringDoctor}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2">
              <strong>Date:</strong> {invoiceDate}
            </Typography>
          </Grid>
        </Grid>

        <Box sx={{ mb: 1.5, p: 1.25, border: "1px solid #e0e0e0", borderRadius: 1 }}>
          {printableItems.map((item, index) => (
            <Box
              key={`${item.testName}-${index}`}
              display="flex"
              justifyContent="space-between"
              sx={{ py: 0.25 }}
            >
              <Typography variant="body2">{item.testName}</Typography>
              <Typography variant="body2">₹{Number(item.amount || 0).toFixed(2)}</Typography>
            </Box>
          ))}
        </Box>

        <InvoiceSummarySection
          subtotal={subTotal}
          gstPercentage={gstPercentage}
          totalAmount={totalAmount}
          paymentModeValue={paymentMode}
          onPaymentModeChange={setPaymentMode}
          paymentDisabled={billClicked || invoiceCreated}
          attachmentField={
            <InvoiceAttachmentControl
              attachmentName={attachmentName}
              attachmentFile={attachmentFile}
              onFileChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                setAttachmentFile(file);
                setAttachmentName(file.name);
              }}
              onDelete={() => {
                setAttachmentFile(null);
                setAttachmentName("");
              }}
              disabled={billClicked || invoiceCreated}
            />
          }
          remarksName="particulars"
          remarksValue={particulars}
          onRemarksChange={(e) => setParticulars(e.target.value)}
          remarksDisabled={billClicked || invoiceCreated}
        />
      </DialogContent>

      <DialogActions
        sx={{
          ...STANDARD_FORM_DIALOG_ACTIONS_SX,
          "@media print": { display: "none" },
        }}
      >
        {invoiceCreated && (
          <Button
            onClick={printInvoice}
            color="primary"
            variant="outlined"
            sx={FORM_CANCEL_BUTTON_SX}
          >
            Print
          </Button>
        )}
        <Button
          onClick={onClose}
          color="primary"
          variant="outlined"
          disabled={billClicked && !invoiceCreated}
          sx={FORM_CANCEL_BUTTON_SX}
        >
          {invoiceCreated ? "Close" : "Cancel"}
        </Button>
        {!invoiceCreated && (
          <Button
            onClick={handleBill}
            color="primary"
            variant="contained"
            disabled={!isBillEnabled()}
            sx={FORM_SUBMIT_BUTTON_SX}
          >
            {billClicked ? "Billing..." : "Bill"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceModal;
